import { useEffect, useState } from 'react';

import { GroupsList } from './GroupsList';
import { Pie } from './Pie';
import { Bubble } from './Bubble';

import {
  aggregateGroups,
  envVar,
  getSymbol,
  getVariation,
} from '../../../helpers/functions';

import { Category } from '../../NewStrategy/types';
import { Center, StrategyAssetsGroup, PieData, BubbleData } from '../types';
import { StrategyAssetsList } from './StrategyAssetsList';
import { useUserStrategies } from '../../../context/UserStrategies';
import { WidgetWithPieModel } from './WidgetWithPie.model';
import { CHART_COLORS_HEX } from '../../../constants/colors';
import { useIntervals } from '../../../hooks/useIntervals';
import { Icon } from '../../UI';
import { useTranslation } from 'react-i18next';
import { TTickerCurrency } from '../../../types/model';
import { CurrencyCodeEnum } from '../../../constants/enums';

export const COLUMNS_ALLOCATION = [
  '1COLUMN',
  'INVESTED',
  'TOT VALUE',
  'ALLOCATION',
  'VARIATION',
] as const;

export const tableColors = Object.keys(CHART_COLORS_HEX);

export const WidgetWithPie = ({
  height,
  width,
  strategyId,
  groupBy,
}: WidgetWithPieModel) => {
  const { t } = useTranslation();
  const { strategies } = useUserStrategies();

  const strategy = strategies.find((s) => s.id === strategyId);
  const items = strategy?.strategyAssets?.items || [];
  const currency = strategy?.currency || CurrencyCodeEnum.USD;

  const symbol = getSymbol(currency);

  const strategyValue = items.reduce(
    (total, { unit, asset: { history } }) =>
      total +
      unit * history[0][currency.toLocaleLowerCase() as TTickerCurrency],
    0
  );

  const [center, setCenter] = useState<Center>({
    id: 'Total',
    value: strategyValue,
  });

  const { interval, IntervalSelector } = useIntervals();

  // const [interval, setInterval] = useState<TimeFrame>(INTERVALS[0]);
  const [groups, setGroups] = useState<StrategyAssetsGroup[]>([]);
  const [chartData, setChartData] = useState<PieData[] | BubbleData>([]);
  const [selectedStrategy, setSelectedStrategy] =
    useState<StrategyAssetsGroup>();
  const [isContinentView, setIsContinentView] = useState(groupBy === 'COUNTRY');

  const columns = isContinentView
    ? ['CONTINENT', ...COLUMNS_ALLOCATION.slice(1)]
    : [...COLUMNS_ALLOCATION];

  const handleSelectStrategy = () => {
    if (groupBy === 'COUNTRY' && isContinentView) {
      setIsContinentView(false);
    } else {
      const group = groups.find(({ title }) => title === center.id);
      setSelectedStrategy(group);
    }
  };

  const resetCenter = () => setCenter({ value: strategyValue, id: 'Total' });

  const handlePieCenter = (i: number) => {
    let idx = groups.findIndex(({ title }) => title === center.id);

    !(idx + i) || idx + i === groups.length
      ? (i = 0)
      : idx + i < 0
      ? (i = groups.length - 1)
      : (i = idx + i);

    const { title: id, totalValue: value } = groups[i];

    setCenter({ id, value });
  };

  useEffect(() => {
    let newGroups = aggregateGroups({
      assets: items,
      currency,
      groupBy,
      withUnit: true,
    }).map((group, i) => ({
      ...group,
      color: tableColors[i],
      currency,
      title: group.title,
    }));

    if (groupBy === 'COUNTRY') {
      !isContinentView &&
        (newGroups = Object.values(
          newGroups.reduce((countries, { assets, region }) => {
            region === center.id &&
              assets.forEach((strategyAsset, i) => {
                const {
                  asset: { country, history, type },
                  entryPrice,
                  purchasedAt,
                  unit,
                } = strategyAsset;
                const key = (country ?? type)!;
                const entryValue = unit * entryPrice;
                const totalValue =
                  unit * history[0][currency.toLocaleLowerCase()];

                const assetTimeFrames = getVariation(
                  currency,
                  history, // series
                  purchasedAt,
                  entryPrice,
                  unit
                );

                countries[key] = countries.hasOwnProperty(key)
                  ? {
                      ...countries[key],
                      assets: [...countries[key].assets, strategyAsset],
                      entryValue: countries[key].entryValue + entryValue,
                      timeFrames: Object.entries(
                        countries[key].timeFrames
                      ).reduce(
                        (res, [resKey, resValue]) => ({
                          ...res,
                          [resKey]: resValue + assetTimeFrames[resKey],
                        }),
                        {} as Category['timeFrames']
                      ),
                      totalValue: countries[key].totalValue + totalValue,
                    }
                  : {
                      assets: [strategyAsset],
                      color: tableColors[i],
                      entryValue,
                      currency,
                      timeFrames: assetTimeFrames,
                      title: key,
                      totalValue,
                    };
              });

            return countries;
          }, {} as { [key: string]: Category & { color: string } })
        ));
    }

    groupBy !== 'COUNTRY' && resetCenter();
    setGroups(newGroups);

    const chartLeaves = newGroups.map(
      ({ title: id, totalValue: value, color }) => ({
        id,
        value,
        color: CHART_COLORS_HEX[color],
      })
    );

    const newChartData =
      groupBy === 'EXCHANGE' || groupBy === 'COUNTRY'
        ? {
            id: 'all',
            children: chartLeaves,
          }
        : chartLeaves;

    setChartData(newChartData);
  }, [items, groupBy, isContinentView]);

  return (
    <div className="flex flex-col h-full w-full bg-white dark:bg-grey-90">
      {/* CLASSES NUMBER AND TIMEFRAMES */}
      <div
        className={`
          flex
          w-full
          items-center
          pl-5
          h-16
          flex-shrink-0
          border-b
          border-grey-10
          overflow-hidden
          dark:border-grey-70
        `}
      >
        <div className="flex text-sm w-1/2 ">
          <span className="text-grey-50 ">
            {t(
              `widgetWithPie.groupBy_${isContinentView ? 'CONTINENT' : groupBy}`
            )}
          </span>
          <span className="font-semibold text-black dark:text-white ml-1 ">
            {groups.length}
          </span>
        </div>

        <div className="flex-none w-1/2 h-16">
          <IntervalSelector className="ml-auto border-l border-grey-10 dark:border-grey-70" />
        </div>
      </div>

      <div
        className={`
          flex
          ${width === 1 && 'flex-col'}
          w-full
          h-full
          overflow-auto
        `}
      >
        {/* PIE */}
        {(width > 1 || height > 1 || (width === 1 && !selectedStrategy)) &&
          groupBy !== 'EXCHANGE' &&
          groupBy !== 'COUNTRY' && (
            <Pie
              center={center}
              className={`
                ${width === 1 ? 'flex mx-auto' : 'flex-none'}
                w-88
                min-h-80
                max-h-80
                sticky
                top-0
                left-0
                relative
              `}
              margin={parseInt(envVar('BASE_FONT_SIZE')) * 2}
              symbol={symbol}
              strategyValue={strategyValue}
              resetCenter={resetCenter}
              setCenter={setCenter}
              pieData={chartData as PieData[]}
              pieColors={Object.values(CHART_COLORS_HEX)}
              onClick={handleSelectStrategy}
              handlePieCenter={handlePieCenter}
            />
          )}

        {/* BUBBLE */}
        {(groupBy === 'EXCHANGE' || groupBy === 'COUNTRY') && (
          <Bubble
            className={`
              ${width === 1 ? 'flex mx-auto' : 'flex-none'}
              w-88
              min-h-80
              max-h-80
              sticky
              top-0
              left-0
            `}
            bubbleData={chartData as BubbleData}
          />
        )}

        {/* TABLE */}
        <div
          className={`
            overflow-x-scroll
            ${
              width > 1
                ? 'w-full border-l border-grey-10'
                : 'border-t border-grey-5'
            }
            dark:border-grey-70
          `}
        >
          {selectedStrategy ? (
            <StrategyAssetsList
              column={width === 1}
              strategy={selectedStrategy}
              interval={interval}
              strategyValue={strategyValue}
              widgetWidth={width}
              onClickArrowBack={() => setSelectedStrategy(undefined)}
            />
          ) : (
            (width > 1 || height > 1) && (
              <GroupsList
                columns={columns}
                column={width === 1}
                groupBy={groupBy}
                center={center}
                groups={groups}
                interval={interval}
                symbol={symbol}
                strategyValue={strategyValue}
                widgetWidth={width}
                ExtraLayer={
                  groupBy === 'COUNTRY' && !isContinentView && !selectedStrategy
                    ? () => (
                        <div
                          className={`
                            flex
                            gap-2
                            col-span-${columns.length}
                            h-14
                            items-center
                            pl-5
                            border-b
                            border-grey-10
                            dark:border-grey-70
                          `}
                        >
                          <Icon
                            className="cursor-pointer"
                            svgName="arrowBack"
                            onClick={() => setIsContinentView(true)}
                          />
                          <div className="font-medium capitalize-first">
                            {t('widget.assetByCountry.backToContinents')}
                          </div>
                        </div>
                      )
                    : undefined
                }
                resetCenter={resetCenter}
                setCenter={setCenter}
                handleSelectStrategy={handleSelectStrategy}
              />
            )
          )}
        </div>
      </div>
    </div>
  );
};
